import { Button, TextArea } from 'components/core';
import { blankNode } from 'helpers/kpiBuilder/advanced';
import { AdvancedQuery } from 'helpers/kpiBuilder/types';
import { AggNodeEditor } from './AggNodeEditor';
import { BuilderSection } from './BuilderSection';
import { TextBox } from './TextBox';
import { OUTLINE_SMALL } from './buttonProps';

interface AdvancedQueryCardProps {
  query: AdvancedQuery;
  index: number;
  onChange: (next: AdvancedQuery) => void;
  onRemove?: () => void;
}


// One query: where it reads, its dashboard filters, and the aggregations it asks for.
export const AdvancedQueryCard = ({ query: q, index, onChange, onRemove }: AdvancedQueryCardProps) => {
  const set = (next: Partial<AdvancedQuery>) => onChange({ ...q, ...next });
  const setMap = (i: number, next: Partial<AdvancedQuery['map'][number]>) => set({ map: q.map.map((f, j) => (j === i ? { ...f, ...next } : f)) });

  return (
    <BuilderSection title={`Query ${index + 1}`} hint="Where this query reads from, the filters the dashboard can apply to it, and the aggregations it asks for. The index name is typed without dev- or prod-.">
      <div className="border rounded p-3 bg-light">
        <TextBox value={q.index} placeholder="Index, e.g. drishti-enriched-user-enrollment" onChange={(value) => set({ index: value })} />
        <div className="mt-2">
          <TextBox value={q.date} placeholder="Date field the dashboard date filter uses, e.g. enrolledDateTimestamp" onChange={(date) => set({ date })} />
        </div>
        <div className="mt-2">
          <TextBox value={q.filterPath} placeholder="Filter path (optional), e.g. aggregations.filtered_data.by_competency.buckets" onChange={(filterPath) => set({ filterPath })} />
        </div>
        <div className="mt-2">
          <TextArea showTitle={false} showDescription={false} rows={2} placeholder="Other top-level keys as JSON, e.g. size 0" value={q.top} onChange={(e: { target: { value: string } }) => set({ top: e.target.value })} />
        </div>
        <div className="mt-3 fw-bold">Dashboard filters for this query</div>
        {q.map.map((f, i) => (
          <div key={i} className="row g-2 mt-1">
            <div className="col-md-5"><TextBox value={f.key} placeholder="Filter name, e.g. District" onChange={(key) => setMap(i, { key })} /></div>
            <div className="col-md-5"><TextBox value={f.field} placeholder="Field, e.g. district" onChange={(field) => setMap(i, { field })} /></div>
            <div className="col-md-2">
              <Button {...OUTLINE_SMALL} onClick={() => set({ map: q.map.filter((_, j) => j !== i) })}>Remove</Button>
            </div>
          </div>
        ))}
        <div className="mt-2">
          <Button {...OUTLINE_SMALL} onClick={() => set({ map: [...q.map, { key: '', field: '', on: '' }] })}>Add a dashboard filter</Button>
        </div>
        <div className="mt-3 fw-bold">Aggregations</div>
        {q.aggs.map((n, i) => (
          <AggNodeEditor key={i} node={n} onChange={(next) => set({ aggs: q.aggs.map((x, j) => (j === i ? next : x)) })} onRemove={() => set({ aggs: q.aggs.filter((_, j) => j !== i) })} />
        ))}
        <div className="mt-2">
          <Button {...OUTLINE_SMALL} className="me-2" onClick={() => set({ aggs: [...q.aggs, blankNode()] })}>Add an aggregation</Button>
          {onRemove && <Button {...OUTLINE_SMALL} onClick={onRemove}>Remove this query</Button>}
        </div>
      </div>
    </BuilderSection>
  );
};
